import { useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuth } from '../../components/AuthProvider';
import { listCampaignOptions } from '../../api/campaigns';
import type { CampaignOption } from '../../api/campaigns';
import { computePayroll, roundMoney, textoTarifas } from '../../domain/payroll';
import type { PayrollLineCalc } from '../../domain/payroll';
import {
  approveClosure, createClosure, getBonoAsistencia, getClosure, listAssignedDays,
  listAttendanceForClosure, listAuthorizationsForClosure, listClosures, listHolidays,
  listPeriodWorkers, getClosurePaymentData,
} from '../../api/payroll';
import type { ClosureDetail, ClosureListItem, ClosureWorker } from '../../api/payroll';
import {
  buildClosureWorkbook, buildPaymentSheetWorkbook, closureFileName, paymentSheetFileName,
} from '../../domain/excel/exportClosure';
import type {
  ClosureExportMeta, PaymentSheetLine, PaymentSheetMeta,
} from '../../domain/excel/exportClosure';
import FilaTarjeta from '../../components/FilaTarjeta';
import { puedeEditar, puedeVer } from '../../domain/permissions';

const TZ = 'America/Lima';

interface PreviewLine extends PayrollLineCalc { worker: ClosureWorker | undefined; }

const soles = (n: number): string =>
  n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Candado() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      strokeWidth="2" aria-hidden="true" className="inline-block align-text-bottom">
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export default function Cierres() {
  const { roles } = useAuth();
  // Crear, aprobar y bajar la planilla con cuentas bancarias es del jefe de operaciones. Dirección (0394) solo mira: ve
  // los cierres y su Excel, que no traen cuentas. El servidor respalda las dos cosas (create_closure, approve_closure y
  // get_closure_payment_data exigen admin).
  const veCierres = puedeVer('cierres', roles);
  const puedeCerrar = puedeEditar('cierres', roles);
  const today = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

  const [tab, setTab] = useState<'nuevo' | 'lista'>('lista');
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [start, setStart] = useState(today.slice(0, 8) + '01');
  const [end, setEnd] = useState(today);
  const [preview, setPreview] = useState<PreviewLine[] | null>(null);
  const [closures, setClosures] = useState<ClosureListItem[]>([]);
  const [detail, setDetail] = useState<ClosureDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  /**
   * Campañas vigentes EN EL PERIODO QUE SE VA A CERRAR, no en el día de hoy.
   *
   * Es la diferencia que importa acá: el cierre de agosto se hace en setiembre, y las campañas que
   * terminaron el 31 de agosto ya no están vigentes hoy. Con «hoy» como criterio, esta pantalla
   * escondería justo la campaña que hay que cerrar.
   */
  useEffect(() => {
    listCampaignOptions(start, end)
      .then((cs) => {
        setCampaigns(cs);
        setCampaignId((actual) => (cs.some((c) => c.id === actual) ? actual : ''));
      })
      .catch(() => setMessage('No se pudieron cargar las campañas.'));
  }, [start, end]);

  useEffect(() => {
    void recargarLista();
  }, []);

  async function recargarLista() {
    try {
      setClosures(await listClosures());
    } catch (e) {
      setMessage('Error al listar cierres: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function calcular() {
    if (!campaignId) { setMessage('Selecciona una campaña.'); return; }
    if (end < start) { setMessage('El fin del periodo no puede ser anterior al inicio.'); return; }
    setBusy(true);
    setMessage('');
    setPreview(null);
    try {
      // Los trabajadores del periodo se piden primero: siembran el cálculo (para que
      // quien solo tuvo una falta justificada aparezca) y evitan repetir la consulta
      // dentro de listAuthorizationsForClosure.
      const periodWorkers = await listPeriodWorkers(campaignId, start, end);
      const periodWorkerIds = periodWorkers.map((w) => w.id);
      const [{ records, workers }, auths, holidays, assignedDays, bonoAsistencia] =
        await Promise.all([
          listAttendanceForClosure(campaignId, start, end),
          listAuthorizationsForClosure(campaignId, start, end, periodWorkerIds),
          // Feriados, días asignados y monto del bono: sin los tres el cálculo pagaría
          // jornada simple en feriado y no pagaría ningún bono (0077).
          listHolidays(start, end),
          listAssignedDays(campaignId, start, end),
          getBonoAsistencia(campaignId),
        ]);
      // El directorio de nombres cubre a los dos grupos; los de attendance mandan.
      const directorio: Record<string, ClosureWorker> = { ...workers };
      for (const w of periodWorkers) {
        if (!directorio[w.id]) directorio[w.id] = w;
      }
      const lines = computePayroll(records, auths, periodWorkerIds, undefined, {
        holidays, bonoAsistencia, assignedDays,
      }).map((l) => ({ ...l, worker: directorio[l.workerId] }));
      setPreview(lines);
      if (lines.length === 0) {
        setMessage('No hay asistencias ni asignaciones aprobadas en ese periodo.');
      }
    } catch (e) {
      setMessage('Error al calcular: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  async function crearBorrador() {
    if (!preview || preview.length === 0) return;
    setBusy(true);
    try {
      const id = await createClosure(campaignId, start, end, preview);
      setPreview(null);
      setMessage('Cierre creado en borrador.');
      await recargarLista();
      await abrirDetalle(id);
      setTab('lista');
    } catch (e) {
      setMessage('Error al crear el cierre: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  async function abrirDetalle(id: string) {
    try {
      setDetail(await getClosure(id));
    } catch (e) {
      setMessage('Error al abrir el cierre: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function aprobar(id: string) {
    if (!window.confirm('Esta acción es definitiva: el cierre quedará aprobado e inmutable. ¿Continuar?')) return;
    setBusy(true);
    try {
      await approveClosure(id);
      setMessage('Cierre aprobado.');
      await recargarLista();
      await abrirDetalle(id);
    } catch (e) {
      setMessage('Error al aprobar: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  function exportar() {
    if (!detail) return;
    const meta: ClosureExportMeta = {
      campaignName: detail.closure.campaign
        ? `${detail.closure.campaign.client} ${detail.closure.campaign.brand}`
        : 'Campaña',
      client: detail.closure.campaign?.client ?? '',
      periodStart: detail.closure.period_start,
      periodEnd: detail.closure.period_end,
    };
    const buffer = buildClosureWorkbook(
      meta,
      detail.lines.map((l) => ({
        dni: l.worker?.dni ?? '',
        workerName: l.worker ? `${l.worker.first_names} ${l.worker.last_names}` : l.worker_id,
        daysWorked: l.days_worked,
        hoursWorked: l.hours_worked,
        amount: l.amount,
        rates: l.rate_breakdown,
        feriado: l.feriado,
        notes: l.notes ?? [],
      })),
    );
    descargar(buffer, closureFileName(meta));
  }

  /** Descarga un libro ya armado. Se comparte entre las dos exportaciones. */
  function descargar(buffer: ArrayBuffer, fileName: string) {
    const url = URL.createObjectURL(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Planilla de pago con el formato del cliente: bloques por supervisor y 16 columnas.
   *
   * Los datos bancarios NO están en el cierre: se piden aparte a `get_closure_payment_data`, que
   * es la única puerta a esas columnas y deja el rastro de quién exportó la planilla y cuándo.
   */
  async function exportarPlanilla() {
    if (!detail) return;
    setBusy(true);
    setMessage('');
    try {
      const pagos = await getClosurePaymentData(detail.closure.id);
      const meta: PaymentSheetMeta = {
        campaignName: detail.closure.campaign
          ? `${detail.closure.campaign.client} ${detail.closure.campaign.brand}`
          : 'Campaña',
        client: detail.closure.campaign?.client ?? '',
        periodStart: detail.closure.period_start,
        periodEnd: detail.closure.period_end,
        analyticAccount: detail.closure.campaign?.analytic_account ?? '',
      };
      const lines: PaymentSheetLine[] = detail.lines.map((l) => {
        const pago = pagos.get(l.worker_id);
        const notes = [...l.notes];
        // Quien cruzó supervisores va a UN solo bloque —una persona, una transferencia— pero
        // el archivo tiene que decirlo: si no, el otro supervisor no encuentra a su gente.
        if (pago?.mixed) {
          notes.push('Trabajó tiendas de más de un supervisor: figura en el bloque donde tuvo más días');
        }
        return {
          documentNumber: pago?.dni ?? l.worker?.dni ?? '',
          documentType: pago?.document_type ?? '',
          workerName: l.worker ? `${l.worker.last_names} ${l.worker.first_names}` : l.worker_id,
          bank: pago?.bank ?? '',
          accountNumber: pago?.account_number ?? '',
          cci: pago?.cci ?? '',
          daysWorked: l.days_worked,
          dailyRate: l.rate_amount,
          dailyRates: l.rate_breakdown.map(([t]) => t),
          bono: l.bono,
          feriado: l.feriado,
          total: l.amount,
          notes,
          supervisor: pago?.supervisor_name ?? '',
        };
      });
      descargar(buildPaymentSheetWorkbook(meta, lines), paymentSheetFileName(meta));
    } catch (e) {
      setMessage(
        'No se pudo armar la planilla de pago: ' + (e instanceof Error ? e.message : String(e)),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!veCierres) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Cierres de pago</h1>
        <p className="mt-2 text-sm text-gray-600">
          Solo el jefe de operaciones (rol admin) puede crear y aprobar cierres.
        </p>
      </div>
    );
  }

  const totalPreview = preview ? roundMoney(preview.reduce((s, l) => s + l.amount, 0)) : 0;
  const totalDetalle = detail ? roundMoney(detail.lines.reduce((s, l) => s + l.amount, 0)) : 0;
  const feriadoPreview = preview ? roundMoney(preview.reduce((s, l) => s + l.feriado, 0)) : 0;
  const feriadoDetalle = detail ? roundMoney(detail.lines.reduce((s, l) => s + l.feriado, 0)) : 0;

  return (
    <div className="p-6">
      <h1 className="mb-4 text-xl font-semibold">Cierres de pago</h1>

      <div className="mb-4 flex gap-2">
        <button onClick={() => setTab('lista')}
          className={`rounded px-3 py-1 text-sm ${tab === 'lista' ? 'bg-blue-600 text-white' : 'border'}`}>
          Cierres
        </button>
        {puedeCerrar && (
          <button onClick={() => setTab('nuevo')}
            className={`rounded px-3 py-1 text-sm ${tab === 'nuevo' ? 'bg-blue-600 text-white' : 'border'}`}>
            Nuevo cierre
          </button>
        )}
      </div>

      {message && <p className="mb-3 text-sm text-gray-700">{message}</p>}

      {tab === 'nuevo' && puedeCerrar && (
        <section className="mb-6">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            {/* min-w-0 + max-w-full: en celular un nombre de campaña largo hacía al select más
                ancho que la pantalla y desplazaba toda la página. En escritorio no cambia. */}
            <label className="flex min-w-0 flex-col text-sm">
              Campaña
              <select className="max-w-full rounded border px-2 py-1" value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}>
                <option value="">— Selecciona —</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.client} — {c.brand}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-sm">
              Inicio del periodo
              <input type="date" className="rounded border px-2 py-1" value={start}
                onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="flex flex-col text-sm">
              Fin del periodo
              <input type="date" className="rounded border px-2 py-1" value={end}
                onChange={(e) => setEnd(e.target.value)} />
            </label>
            <button onClick={calcular} disabled={busy}
              className="rounded bg-blue-600 px-4 py-1.5 text-white disabled:opacity-50">
              {busy ? 'Calculando…' : 'Calcular'}
            </button>
          </div>

          {preview && preview.length > 0 && (
            <>
              {/* En celular: una tarjeta por línea del cierre (sin desplazamiento horizontal). */}
              <div className="space-y-2 lg:hidden">
                {preview.map((l) => (
                  <FilaTarjeta
                    key={l.workerId}
                    titulo={l.worker ? `${l.worker.firstNames} ${l.worker.lastNames}` : l.workerId}
                    campos={[
                      { etiqueta: 'DNI', valor: l.worker?.dni },
                      { etiqueta: 'Días', valor: l.daysWorked },
                      { etiqueta: 'Horas', valor: l.hoursWorked },
                      { etiqueta: 'Tarifa(s) S/', valor: textoTarifas(l.rateBreakdown) || '—' },
                      { etiqueta: 'Feriado S/', valor: soles(l.feriado) },
                      { etiqueta: 'Monto S/', valor: soles(l.amount) },
                      { etiqueta: 'Observaciones', valor: l.notes.join('; ') },
                    ]}
                  />
                ))}
                <p className="rounded border bg-gray-50 p-3 text-sm font-semibold">
                  Total: S/ {soles(totalPreview)}
                </p>
              </div>

              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 text-left">DNI</th>
                      <th className="p-2 text-left">Trabajador</th>
                      <th className="p-2">Días</th>
                      <th className="p-2">Horas</th>
                      <th className="p-2">Tarifa(s) S/</th>
                      <th className="p-2">Feriado S/</th>
                      <th className="p-2">Monto S/</th>
                      <th className="p-2 text-left">Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((l) => (
                      <tr key={l.workerId} className="border-t">
                        <td className="p-2">{l.worker?.dni ?? '—'}</td>
                        <td className="p-2">
                          {l.worker ? `${l.worker.firstNames} ${l.worker.lastNames}` : l.workerId}
                        </td>
                        <td className="p-2 text-center">{l.daysWorked}</td>
                        <td className="p-2 text-center">{l.hoursWorked}</td>
                        <td className="p-2 text-center">{textoTarifas(l.rateBreakdown) || '—'}</td>
                        <td className="p-2 text-right">{l.feriado > 0 ? soles(l.feriado) : '—'}</td>
                        <td className="p-2 text-right">{soles(l.amount)}</td>
                        <td className="p-2 text-gray-600">{l.notes.join('; ')}</td>
                      </tr>
                    ))}
                    <tr className="border-t bg-gray-50 font-semibold">
                      <td className="p-2" colSpan={5}>Total</td>
                      <td className="p-2 text-right">{soles(feriadoPreview)}</td>
                      <td className="p-2 text-right">{soles(totalPreview)}</td>
                      <td className="p-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button onClick={crearBorrador} disabled={busy}
                className="mt-3 rounded bg-green-600 px-4 py-1.5 text-white disabled:opacity-50">
                Crear borrador
              </button>
            </>
          )}
        </section>
      )}

      {tab === 'lista' && (
        <section className="mb-6">
          {/* En celular: una tarjeta por cierre (sin desplazamiento horizontal). */}
          <div className="space-y-2 lg:hidden">
            {closures.map((c) => (
              <FilaTarjeta
                key={c.id}
                titulo={c.campaign ? `${c.campaign.client} — ${c.campaign.brand}` : '—'}
                estados={
                  c.status === 'aprobado' ? (
                    <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                      <Candado /> Aprobado
                    </span>
                  ) : (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                      Borrador
                    </span>
                  )
                }
                campos={[
                  { etiqueta: 'Periodo', valor: `${c.period_start} a ${c.period_end}` },
                  {
                    etiqueta: 'Creado',
                    valor: formatInTimeZone(c.created_at, TZ, 'dd/MM/yyyy HH:mm'),
                  },
                ]}
                acciones={
                  <button onClick={() => abrirDetalle(c.id)} className="text-blue-600 underline">
                    Ver detalle
                  </button>
                }
              />
            ))}
            {closures.length === 0 && (
              <p className="rounded border p-4 text-center text-gray-500">
                Aún no hay cierres. Crea uno en "Nuevo cierre".
              </p>
            )}
          </div>

          <div className="hidden overflow-x-auto lg:block">
          <table className="min-w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">Campaña</th>
                <th className="p-2">Periodo</th>
                <th className="p-2">Estado</th>
                <th className="p-2">Creado</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {closures.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="p-2">
                    {c.campaign ? `${c.campaign.client} — ${c.campaign.brand}` : '—'}
                  </td>
                  <td className="p-2 text-center">{c.period_start} a {c.period_end}</td>
                  <td className="p-2 text-center">
                    {c.status === 'aprobado' ? (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                        <Candado /> Aprobado
                      </span>
                    ) : (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                        Borrador
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    {formatInTimeZone(c.created_at, TZ, 'dd/MM/yyyy HH:mm')}
                  </td>
                  <td className="p-2 text-center">
                    <button onClick={() => abrirDetalle(c.id)} className="text-blue-600 underline">
                      Ver detalle
                    </button>
                  </td>
                </tr>
              ))}
              {closures.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-gray-500">
                    Aún no hay cierres. Crea uno en "Nuevo cierre".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </section>
      )}

      {detail && (
        <section className="rounded border bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">
              {detail.closure.campaign
                ? `${detail.closure.campaign.client} — ${detail.closure.campaign.brand}`
                : 'Cierre'}{' '}
              · {detail.closure.period_start} a {detail.closure.period_end}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {detail.closure.status === 'borrador' && !puedeCerrar ? (
                <span className="text-sm text-gray-600">Borrador · solo lectura</span>
              ) : detail.closure.status === 'borrador' ? (
                <button onClick={() => aprobar(detail.closure.id)} disabled={busy}
                  className="rounded bg-green-600 px-3 py-1 text-sm text-white disabled:opacity-50">
                  Aprobar cierre
                </button>
              ) : (
                <span className="text-sm text-gray-600">
                  <Candado /> Aprobado el{' '}
                  {detail.closure.approved_at
                    ? formatInTimeZone(detail.closure.approved_at, TZ, 'dd/MM/yyyy HH:mm')
                    : '—'}{' '}
                  · solo lectura
                </span>
              )}
              {puedeCerrar && (
                <button onClick={() => void exportarPlanilla()} disabled={busy}
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50">
                  Planilla de pago
                </button>
              )}
              <button onClick={exportar} className="rounded border px-3 py-1 text-sm">
                Exportar a Excel
              </button>
              <button onClick={() => setDetail(null)} className="rounded border px-3 py-1 text-sm">
                Cerrar
              </button>
            </div>
          </div>

          {/* En celular: una tarjeta por línea del cierre (sin desplazamiento horizontal). */}
          <div className="space-y-2 lg:hidden">
            {detail.lines.map((l) => (
              <FilaTarjeta
                key={l.id}
                titulo={l.worker ? `${l.worker.first_names} ${l.worker.last_names}` : l.worker_id}
                campos={[
                  { etiqueta: 'DNI', valor: l.worker?.dni },
                  { etiqueta: 'Días', valor: l.days_worked },
                  { etiqueta: 'Horas', valor: l.hours_worked },
                  { etiqueta: 'Tarifa(s) S/', valor: textoTarifas(l.rate_breakdown) || '—' },
                  { etiqueta: 'Feriado S/', valor: soles(l.feriado) },
                  { etiqueta: 'Monto S/', valor: soles(l.amount) },
                  { etiqueta: 'Observaciones', valor: (l.notes ?? []).join('; ') },
                ]}
              />
            ))}
            <p className="rounded border bg-gray-50 p-3 text-sm font-semibold">
              Total: S/ {soles(totalDetalle)}
            </p>
          </div>

          <div className="hidden overflow-x-auto lg:block">
            <table className="min-w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">DNI</th>
                  <th className="p-2 text-left">Trabajador</th>
                  <th className="p-2">Días</th>
                  <th className="p-2">Horas</th>
                  <th className="p-2">Tarifa(s) S/</th>
                  <th className="p-2">Feriado S/</th>
                  <th className="p-2">Monto S/</th>
                  <th className="p-2 text-left">Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((l) => (
                  <tr key={l.id} className="border-t">
                    <td className="p-2">{l.worker?.dni ?? '—'}</td>
                    <td className="p-2">
                      {l.worker ? `${l.worker.first_names} ${l.worker.last_names}` : l.worker_id}
                    </td>
                    <td className="p-2 text-center">{l.days_worked}</td>
                    <td className="p-2 text-center">{l.hours_worked}</td>
                    <td className="p-2 text-center">{textoTarifas(l.rate_breakdown) || '—'}</td>
                    <td className="p-2 text-right">{l.feriado > 0 ? soles(l.feriado) : '—'}</td>
                    <td className="p-2 text-right">{soles(l.amount)}</td>
                    <td className="p-2 text-gray-600">{(l.notes ?? []).join('; ')}</td>
                  </tr>
                ))}
                <tr className="border-t bg-gray-50 font-semibold">
                  <td className="p-2" colSpan={5}>Total</td>
                  <td className="p-2 text-right">{soles(feriadoDetalle)}</td>
                  <td className="p-2 text-right">{soles(totalDetalle)}</td>
                  <td className="p-2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
